import './QuantityPage.scss';

import * as XLSX from 'xlsx';

import { Box } from "@mui/material";

import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

import { useEffect } from "react";
import { useState } from "react";
import { useMemo } from "react";

import { fetchQuantity } from "../../slices/quantitySlice";

import { LargeTitle } from "../../common-components/Titles/Titles";
import { TextButton } from "../../common-components/Buttons/Buttons";

import Quantity from "../../components/Quantitys/Quantity";
import QuantityFilters from "../../components/QuantityFilters/QuantityFilters";
import {endOfMonth, endOfWeek, endOfYear, startOfMonth, startOfWeek, startOfYear} from "date-fns";

const QuantityPage = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.quantity.data);
    const token = useSelector((state) => state.auth.token?.data?.token || state.auth.token);

    const [reset, setReset] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [filterParams, setFilterParams] = useState({});
    const [, setSelectedMonth] = useState(null);
    const [, setSelectedYear] = useState(null);

    const emergencyTypes = [
        { id: "1", name: "Пожар" },
        { id: "2", name: "Травма" },
        { id: "3", name: "Отключение электроэнергии" },
        { id: "4", name: "Обрушение" },
        { id: "5", name: "Несчастный случай" },
        { id: "6", name: "Застревание" },
        { id: "7", name: "Заболевание" },
        { id: "8", name: "Затопление" },
        { id: "9", name: "ДТП" },
        { id: "10", name: "Загазованность" },
        { id: "11", name: "Взрыв" },
        { id: "12", name: "Обеспечение противопожарной" },
        { id: "13", name: "Поиск людей" },
        { id: "14", name: "Открытый фонтан" },
        { id: "15", name: "Розлив" },
        { id: "16", name: "Иное" },
    ];

    const branchOffices = [
        { branch_office_id: "1", name: "Актюбинский" },
        { branch_office_id: "2", name: "Атырауский" },
        { branch_office_id: "3", name: "ВКФ" },
        { branch_office_id: "4", name: "ЗКФ" },
        { branch_office_id: "5", name: "Карагандинский" },
        { branch_office_id: "6", name: "Кызылординский" },
        { branch_office_id: "7", name: "Мангистауский" },
        { branch_office_id: "8", name: "Павлодарский" },
        { branch_office_id: "9", name: "СКФ" },
        { branch_office_id: "10", name: "ЮКФ" },
        { branch_office_id: "11", name: "Акмолинский" },
    ];

    // Собираем querystring аккуратно
    const params = useMemo(() => {
        const p = new URLSearchParams();

        // массивы вида emergency_type[]=id
        if (Array.isArray(filterParams.emergency_type) && filterParams.emergency_type.length > 0) {
            for (const id of filterParams.emergency_type) {
                p.append("emergency_type[]", id);
            }
        }

        // остальные поля
        Object.entries(filterParams).forEach(([key, value]) => {
            if (key === "emergency_type") return;
            if (value != null && value !== "") {
                p.append(key, value);
            }
        });

        p.append("page", String(currentPage));
        return p.toString();
    }, [filterParams, currentPage]);

    useEffect(() => {
        dispatch(fetchQuantity({ token, params }));
    }, [dispatch, token, params]);

    // ======== Handlers (каждый сбрасывает страницу на 1) ========

    const handleRangeChange = ({ startDate = "", endDate = "" } = {}) => {
        if (startDate) {
            setFilterParams((prev) => ({
                ...prev,
                date_start: new Date(startDate).toISOString(),
                date_end: endDate ? new Date(endDate).toISOString() : undefined,
            }));
            setCurrentPage(1);
        }
    };

    const handleYearChange = (yearId) => {
        const year = parseInt(yearId, 10);
        if (isNaN(year)) return;

        const dateStart = startOfYear(new Date(year, 0, 1));
        const dateEnd = endOfYear(new Date(year, 0, 1));

        setSelectedYear(year);
        setFilterParams((prev) => ({
            ...prev,
            date_start: dateStart.toISOString(),
            date_end: dateEnd.toISOString(),
        }));
        setCurrentPage(1);
    };

    const handleMonthChange = (monthId) => {
        // из UI часто приходит 1..12, а в Date нужен 0..11
        const month = parseInt(monthId, 10);
        if (isNaN(month)) return;

        const today = new Date();
        const year = today.getFullYear();

        const zeroBasedMonth = Math.max(0, Math.min(11, month - 1));
        const dateStart = startOfMonth(new Date(year, zeroBasedMonth, 1));
        const dateEnd = endOfMonth(new Date(year, zeroBasedMonth, 1));

        setSelectedMonth(month);
        setFilterParams((prev) => ({
            ...prev,
            date_start: dateStart.toISOString(),
            date_end: dateEnd.toISOString(),
        }));
        setCurrentPage(1);
    };

    const handleWeekChange = () => {
        const today = new Date();
        const range = {
            date_start: startOfWeek(today, { weekStartsOn: 1 }),
            date_end: endOfWeek(today, { weekStartsOn: 1 }),
        };
        setFilterParams((prev) => ({
            ...prev,
            date_start: range.date_start.toISOString(),
            date_end: range.date_end.toISOString(),
        }));
        setCurrentPage(1);
    };

    const handleEmergencyChange = (emergencyId) => {
        setFilterParams((prev) => {
            const prevArr = Array.isArray(prev.emergency_type) ? prev.emergency_type : [];
            const has = prevArr.includes(emergencyId);
            const nextArr = has ? prevArr.filter((id) => id !== emergencyId) : [...prevArr, emergencyId];

            return { ...prev, emergency_type: nextArr };
        });
        setCurrentPage(1);
    };

    const handleTypeChange = (isEmergencyLine) => {
        // Если бэку нужно 0/1 — раскомментируй след. строку
        // const value = isEmergencyLine ? 1 : 0;
        const value = isEmergencyLine;
        setFilterParams((prev) => ({ ...prev, is_emergency_line: value }));
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilterParams({});
        setSelectedMonth(null);
        setSelectedYear(null);
        setCurrentPage(1);
        setReset((prev) => !prev);
    };

    // ======== Export ========
    const handleExport = () => {
        if (!data || !data.cells) return;

        const groupedByEmergency = data.cells.reduce((acc, cell) => {
            if (!acc[cell.emergency_type]) acc[cell.emergency_type] = {};
            acc[cell.emergency_type][cell.branch_office_id] = cell.count;
            return acc;
        }, {});

        const rows = emergencyTypes.map((type) => {
            const branchData = branchOffices.map(
                (office) => groupedByEmergency[type.id]?.[office.branch_office_id] || 0
            );
            const totalForType = data.total_per_type?.[type.id] ?? 0;

            return {
                "Наименование аварийной ситуации": type.name,
                ...branchOffices.reduce((acc, office, index) => {
                    acc[office.name] = branchData[index];
                    return acc;
                }, {}),
                "Всего": totalForType,
            };
        });

        rows.push({
            "Наименование аварийной ситуации": "Всего зарегистрировано",
            ...branchOffices.reduce((acc, office) => {
                acc[office.name] = data.total_per_branch_office?.[office.branch_office_id] ?? 0;
                return acc;
            }, {}),
            "Всего": data.overall_total ?? 0,
        });

        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Quantity Data");
        ws["!cols"] = [{ wpx: 300 }, ...branchOffices.map(() => ({ wpx: 100 })), { wpx: 100 }];
        XLSX.writeFile(wb, "quantity_data.xlsx");
    };

    return (
        <Box className="quantity_page">
            <Box className="quantity_page_container">
                <Box className="quantity_page_container_header">
                    <Box className="quantity_page_container_header_title">
                        <LargeTitle property="Количество выездов по видам" />
                    </Box>
                    <Box className="quantity_page_container_header_button">
                        <TextButton title="Экспортировать" onClick={handleExport} active />
                    </Box>
                </Box>

                <Box className="quantity_page_container_search">
                    <QuantityFilters
                        onRangeChange={handleRangeChange}
                        onEmergencyChange={handleEmergencyChange}
                        onWeekChange={handleWeekChange}
                        onMonthChange={handleMonthChange}
                        onYearChange={handleYearChange}
                        onTypeChange={handleTypeChange}
                        onReset={resetFilters}
                        reset={reset}
                    />
                </Box>

                <Box className="quantity_page_container_table">
                    <Quantity />
                </Box>
            </Box>
        </Box>
    );
};

export default QuantityPage;